import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageHero from '../components/layout/PageHero';
import SEO from '../components/SEO';
import {
    ArrowRight,
    Scale,
    ShieldCheck,
    Stethoscope,
    Briefcase,
    Glasses,
    Building2,
    Trash2,
    Lock,
    HeartPulse,
    Baby,
    BrainCircuit,
    UserX,
    ClipboardCheck,
    Globe,
    GraduationCap,
    Users,
    CreditCard,
    Siren,
    Gavel,
    UserCheck,
    AlertTriangle,
    TrendingUp,
    Store
} from 'lucide-react';
import './Issues.css';

// Mapping Issue IDs to Icons
const iconMap = {
    'pays-for-itself': Scale,
    'admin-waste-myth': Trash2,
    'what-safecare-covers': ShieldCheck,
    'dental-vision': Glasses,
    'medical-necessity': ClipboardCheck,
    'universal-floor': Globe,
    'fair-financing': Scale,
    'fair-education': GraduationCap,
    'real-choice': Users,
    'medical-debt': CreditCard,
    'waste-fraud': Siren,
    'safety-net': UserX,
    'national-security': ShieldCheck,
    'medical-malpractice': Gavel,
    'medical-workforce': UserCheck,
    'critique': AlertTriangle,
    'not-socialism': Building2,
    'small-business': Store,
    'cost-timeline': TrendingUp,
    'hospital-solvency': HeartPulse,
    'life-by-thousand-cuts': Scale,
};

export default function Issues() {
    const [issuesData, setIssuesData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('All');

    useEffect(() => {
        fetch('/data/issues_index.json')
            .then(res => res.json())
            .then(data => {
                setIssuesData(data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed to load issues index", err);
                setLoading(false);
            });
    }, []);

    if (loading) return <div style={{ textAlign: 'center', padding: '5rem', color: '#fff' }}>Loading Knowledge Base...</div>;
    if (!issuesData) return null;

    // Get unique categories for filter tabs
    const categories = ['All', ...new Set(issuesData.issues.map(i => i.category || 'System'))];

    const filteredIssues = activeFilter === 'All'
        ? issuesData.issues
        : issuesData.issues.filter(i => (i.category || 'System') === activeFilter);

    return (
        <div className="issues-page">
            <SEO
                title="Knowledge Base - SAFEC+"
                description="Plain talk on the 15 biggest questions about SAFEC+. From coverage to costs, dental care to medical necessity."
                image="/image/issue_coverage.jpg"
            />
            <PageHero
                title="Knowledge Base"
                subtitle="The details behind the debate. Plain talk on the biggest questions."
                image="/image/issue_financing.jpg"
                badge="Encyclopedia of Reform"
            />

            <div className="container" style={{ marginTop: '-4rem', position: 'relative', zIndex: 20 }}>

                {/* Filter Bar */}
                <div className="issue-filters glass-panel">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            className={`filter-btn ${activeFilter === cat ? 'active' : ''}`}
                            onClick={() => setActiveFilter(cat)}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                <div className="issues-grid">
                    {filteredIssues.map((issue, index) => (
                        <motion.div
                            key={issue.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.05 }}
                        >
                            <Link
                                to={issue.type === 'link' ? issue.linkTarget : `/issues/${issue.id}`}
                                style={{ textDecoration: 'none' }}
                            >
                                <div className="issue-card glass-panel">
                                    <div className="issue-card-image-wrapper">
                                        <img src={issue.image} alt={issue.title} className="issue-card-img" />
                                        <div className={`category-badge cat-${(issue.category || 'System').toLowerCase()}`}>
                                            {issue.category || 'System'}
                                        </div>
                                        {iconMap[issue.id] && (
                                            <div className="issue-icon-overlay">
                                                {(() => {
                                                    const Icon = iconMap[issue.id];
                                                    return <Icon size={24} />;
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                    <div className="issue-card-content">
                                        <div className="issue-header-row">
                                            <h2>{issue.title}</h2>
                                        </div>
                                        <p>{issue.summary}</p>
                                        <span className="issue-link-btn">
                                            Learn More <ArrowRight size={16} />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}
